import React, { useEffect, useState } from 'react';
import Checkbox from '@cloudscape-design/components/checkbox';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import { api } from '../api/client';
import { useNotifications } from '../hooks/useNotifications';
import { PolicyModal } from './PolicyModal';
import { Team, TEAM_ID_PREFIX, validateTeamId, validateTeamName } from './teams';

interface TeamModalProps {
  visible: boolean;
  /** The team to rename. Omit to register a new team. */
  team?: Team;
  /** Registered teams, used to catch duplicates before calling the service. */
  teams: Team[];
  onCancel: () => void;
  /** Called after a successful save, with the team as saved and whether its TEAM user was also created. */
  onSaved: (team: Team, userCreated: boolean) => void;
}

/** Register a team (name + ID, optionally also creating its TEAM user) or rename an existing one. */
export const TeamModal: React.FC<TeamModalProps> = ({ visible, team, teams, onCancel, onSaved }) => {
  const renaming = !!team;
  const [teamName, setTeamName] = useState('');
  const [teamId, setTeamId] = useState('');
  const [createUser, setCreateUser] = useState(true);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addNotification } = useNotifications();

  useEffect(() => {
    if (!visible) return;
    setTeamName(team?.team_name ?? '');
    setTeamId(team?.team_id ?? '');
    setCreateUser(true);
    setTouched(false);
    setError(null);
  }, [visible, team]);

  const nameError = validateTeamName(teamName, teams, team);
  const idError = renaming ? null : validateTeamId(teamId, teams);
  const unchanged = renaming && teamName.trim() === team!.team_name;

  const save = async () => {
    setTouched(true);
    if (nameError || idError || unchanged) return;
    const saved: Team = { team_id: teamId.trim(), team_name: teamName.trim() };
    setBusy(true);
    setError(null);
    try {
      if (renaming) {
        await api('PUT', '/team_user', saved);
        addNotification('success', `Renamed team to '${saved.team_name}'`);
      } else {
        await api('POST', '/team_user', saved);
        if (createUser) {
          try {
            await api('POST', '/user', { type: 'TEAM', value: saved.team_id });
          } catch (e) {
            // The team is registered; only the follow-up user creation failed. Report it and
            // still close, so a retry does not hit "already registered".
            addNotification(
              'error',
              `Team '${saved.team_name}' was registered, but creating its TEAM user failed: ${(e as Error).message}`,
            );
            onSaved(saved, false);
            return;
          }
        }
        addNotification(
          'success',
          createUser ? `Registered team '${saved.team_name}' and created its TEAM user` : `Registered team '${saved.team_name}'`,
        );
      }
      onSaved(saved, !renaming && createUser);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <PolicyModal
      visible={visible}
      header={renaming ? 'Rename team' : 'Register team'}
      confirmText={renaming ? 'Rename' : 'Register'}
      busy={busy}
      confirmDisabled={unchanged}
      error={error}
      errorHeader={renaming ? 'Could not rename team' : 'Could not register team'}
      onCancel={onCancel}
      onConfirm={save}
    >
      <FormField label="Team name" errorText={touched ? nameError : undefined}>
        <Input
          value={teamName}
          onChange={({ detail }) => setTeamName(detail.value)}
          placeholder="e.g. labfin-dev"
          ariaLabel="Team name"
        />
      </FormField>
      <FormField
        label="Team ID"
        description={
          renaming
            ? 'The team ID cannot be changed: users and policies refer to it.'
            : `Paste the team's Amazon team ID. It starts with ${TEAM_ID_PREFIX}`
        }
        errorText={touched ? idError : undefined}
      >
        <Input
          value={teamId}
          onChange={({ detail }) => setTeamId(detail.value)}
          placeholder={`${TEAM_ID_PREFIX}...`}
          disabled={renaming}
          ariaLabel="Team ID"
        />
      </FormField>
      {!renaming && (
        <Checkbox checked={createUser} onChange={({ detail }) => setCreateUser(detail.checked)}>
          Also create a TEAM user for this team
        </Checkbox>
      )}
    </PolicyModal>
  );
};
